import { MenuItem, TextField } from "@leap4/frontend";
import React, { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useGetAllCampaigns } from "../../../api/generated/api";

/** Falls back to this until someone picks otherwise. */
export const DEFAULT_CAMPAIGN = "mb";

/**
 * The selected campaign lives in the query string, so a tab switch keeps it and a link carries it.
 */
export const useCampaign = (): [string, (next: string) => void] => {
    const history = useHistory();
    const location = useLocation();
    const campaign = new URLSearchParams(location.search).get("campaign") || DEFAULT_CAMPAIGN;

    const setCampaign = useCallback(
        (next: string) => {
            const params = new URLSearchParams(location.search);
            if (next === DEFAULT_CAMPAIGN) params.delete("campaign");
            else params.set("campaign", next);
            history.replace({ search: params.toString() });
        },
        [history, location.search]
    );

    return [campaign, setCampaign];
};

const CampaignSelect = ({
    campaign,
    onChange,
}: {
    campaign: string;
    onChange: (next: string) => void;
}): JSX.Element => {
    const { data } = useGetAllCampaigns(undefined, {
        query: { staleTime: 600000, refetchOnWindowFocus: false },
    });

    // The selected one is always offered, so the control never renders blank while the list loads.
    const options = useMemo(() => {
        const ids = (data?.data ?? []).map((c) => c.id_campaign).filter(Boolean);
        return ids.includes(campaign) ? ids : [campaign, ...ids];
    }, [data, campaign]);

    return (
        <TextField
            select
            size="small"
            label="Campaign"
            value={campaign}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 130, flexShrink: 0 }}
        >
            {options.map((id) => (
                <MenuItem key={id} value={id}>
                    {id}
                </MenuItem>
            ))}
        </TextField>
    );
};

export default CampaignSelect;
